import { Box, CircularProgress, Typography, useTheme } from "@mui/material"
import { useNavigate } from "react-router-dom"
import Iconify from "minimals-template/components/Iconify"
import WidgetCard from "lib/widget-card"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { toDataCy } from "lib/to-data-cy"

/**
 * A clickable stat tile for the authoring homepage.
 *
 * @param {string} title - Label shown below the count
 * @param {number|symbol} count - The count to display, or IS_LOADING while fetching
 * @param {string} icon - Iconify icon name
 * @param {"primary"|"secondary"|"warning"|"error"|"info"|"success"} palette - MUI palette key
 * @param {string} to - Navigation path on click
 */
export function AuthoringHomeStatTile({ title, pluralTitle, count, icon, palette = "primary", to }) {
    const theme = useTheme()
    const navigate = useNavigate()
    const color = theme.palette[palette]?.main ?? theme.palette.primary.main

    return (
        <WidgetCard
            component="button"
            onClick={handleClick}
            bannerColor={color}
            data-cy={toDataCy("authoring-home-tile", title)}
            sx={{ cursor: "pointer", "&:hover": { boxShadow: 6 }, transition: "box-shadow 0.2s" }}
        >
            <Box display="flex" flexDirection="column" gap={1.5} pt={1} width={1}>
                <Iconify icon={icon} sx={{ color, width: 36, height: 36 }} />
                <Box>
                    {count === IS_LOADING ? (
                        <CircularProgress size={28} sx={{ color }} />
                    ) : (
                        <Typography variant="h3" fontWeight="bold" lineHeight={1}>
                            {count ?? "—"}
                        </Typography>
                    )}
                    <Typography variant="subtitle1" sx={{ mt: 0.5, opacity: 0.72 }}>
                        {count === IS_LOADING || count === 1 ? title : pluralTitle ?? title.pluralize()}
                    </Typography>
                </Box>
            </Box>
        </WidgetCard>
    )

    function handleClick() {
        navigate(to)
    }
}
